import { toast } from "@/components/ui/use-toast";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError, handleApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { convertToPostNetworkRequest } from "../-schema/add-network-schema";
import type { AddNetworkFormData } from "../-schema/add-network-schema";
import { useAddNetworkFormStore } from "../-store/add-network-form-store";

// 共通のエラーハンドラに委譲
function extractErrorMessage(error: unknown, language: string): string {
  const { message } = handleApiError(error, { language });
  return message;
}

export const useCreateNetwork = () => {
  const navigate = useNavigate();
  const store = useAddNetworkFormStore();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.add",
  });
  const mutation = useMutation({
    mutationFn: async () => {
      const formData: AddNetworkFormData = {
        networkName: store.networkName,
        networkType: store.networkType,
        connectionType: store.connectionType,
        networkCategory: store.networkCategory,
        destinationType: store.destinationType,
        placeId: store.placeId,
        deviceId: store.deviceId,
        purpose: store.purpose,
        adminContact: store.adminContact,
        accessLevel: store.accessLevel,
        accessStatus: store.accessStatus,
        accessPeriodStartDate: store.accessPeriodStartDate,
        accessPeriodEndDate: store.accessPeriodEndDate,
        subjectType: store.subjectType,
      };
      const request = convertToPostNetworkRequest(formData);
      const res = await apiClient.POST("/v2/networks", { body: request });
      return getDataOrThrowApiError(res);
    },
    onSuccess: (data) => {
      toast({
        title: t("toast.success.title"),
        description: t("toast.success.description"),
      });
      queryClient.invalidateQueries({ queryKey: [["networks"], "list"] });
      navigate({ to: "/networks/$id", params: { id: data.id } });
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error, i18n.language);
      toast({
        title: t("toast.error.title"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const submitNetwork = async () => {
    if (!store.validate()) {
      toast({
        title: t("toast.validationError.title"),
        description: t("toast.validationError.description"),
        variant: "destructive",
      });
      return;
    }

    await mutation.mutateAsync();
  };

  return {
    submitNetwork,
    isSubmitting: mutation.isPending,
  };
};
